import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-between">
              <Link 
                to="/" 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center gap-3 cursor-pointer"
              >
                <img 
                  src="https://static.readdy.ai/image/b0456826ee89ea952d83ed53f01c22ea/e364b9ef8d395931045c47454dbe130a.png" 
                  alt="El Papá Profesor" 
                  className="h-[94px] w-auto"
                />
              </Link>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#metodologia" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium cursor-pointer">Metodología</a>
              <a href="#testimonios" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium cursor-pointer">Testimonios</a>
              <a href="#planes" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium cursor-pointer">Planes</a>
              <a href="#comunidad" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium cursor-pointer">Comunidad</a>
              <a href="#faq" className="text-gray-700 hover:text-orange-600 transition-colors text-sm font-medium cursor-pointer">FAQ</a>
            </div>
            <div className="flex items-center gap-3">
              <a 
                href="#planes" 
                className="hidden sm:inline-flex bg-orange-600 text-white px-6 py-2.5 rounded-lg hover:bg-orange-700 transition-all text-sm font-semibold whitespace-nowrap cursor-pointer"
              >
                Ver Planes
              </a>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                aria-label="Abrir menú"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl text-gray-700`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-[90] md:hidden"
            onClick={closeMobileMenu}
          />
        )}

        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="bg-white absolute top-full left-0 w-full h-auto flex flex-col items-center justify-start gap-5 py-8 shadow-2xl z-[100] overflow-visible md:hidden">
            <a 
              href="#metodologia" 
              onClick={closeMobileMenu}
              className="block text-slate-900 text-lg font-semibold hover:text-orange-600 transition-colors cursor-pointer"
            >
              Metodología
            </a>
            <a 
              href="#testimonios" 
              onClick={closeMobileMenu}
              className="block text-slate-900 text-lg font-semibold hover:text-orange-600 transition-colors cursor-pointer"
            >
              Testimonios
            </a>
            <a 
              href="#planes" 
              onClick={closeMobileMenu}
              className="block text-slate-900 text-lg font-semibold hover:text-orange-600 transition-colors cursor-pointer"
            >
              Planes
            </a>
            <a 
              href="#comunidad" 
              onClick={closeMobileMenu}
              className="block text-slate-900 text-lg font-semibold hover:text-orange-600 transition-colors cursor-pointer"
            >
              Comunidad
            </a>
            <a 
              href="#faq" 
              onClick={closeMobileMenu}
              className="block text-slate-900 text-lg font-semibold hover:text-orange-600 transition-colors cursor-pointer"
            >
              FAQ
            </a>
            <a 
              href="#planes" 
              onClick={closeMobileMenu}
              className="bg-orange-600 text-white px-8 py-3 rounded-lg hover:bg-orange-700 transition-all text-base font-semibold whitespace-nowrap cursor-pointer shadow-lg mt-2"
            >
              Ver Planes
            </a>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-orange-600 rounded-full bg-white">
                <i className="ri-calendar-line text-orange-600 text-lg"></i>
                <span className="text-sm font-semibold text-gray-800">Programas Marzo 2026</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-extrabold text-gray-800 leading-tight">
                Que el colegio deje de ser un <span className="text-orange-600">campo de batalla</span>. Construyamos una <span className="text-orange-600">casa segura</span> este 2026.
              </h1>
              
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
                Mucho más que clases particulares. Un acompañamiento académico estructurado para estudiantes de Básica y Media. Recupera la paz mental en tu hogar y deja de apagar incendios a última hora.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://wa.me/56912345678?text=Hola%2C%20quiero%20solicitar%20un%20diagn%C3%B3stico%20gratuito%20para%20mi%20hijo%2Fa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-lg hover:bg-orange-700 transition-all text-sm font-semibold shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Solicitar Diagnóstico Gratuito
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>
                <a 
                  href="#comunidad" 
                  className="inline-flex items-center justify-center gap-2 border-2 border-orange-600 text-orange-600 px-8 py-4 rounded-lg hover:bg-orange-50 transition-all text-sm font-semibold whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                  Unirse a la Comunidad
                </a>
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://public.readdy.ai/ai/img_res/13d11e440849547dc18eb5d530e40bf4.jpg" 
                  alt="Padre ayudando a hijo con tareas" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problema Section */}
      <section className="py-20 px-6 bg-amber-50/50">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl p-12 shadow-sm" style={{ backgroundColor: '#FFF0E5' }}>
            <div className="flex justify-center mb-6">
              <div className="w-24 h-24 flex items-center justify-center">
                <img 
                  src="https://static.readdy.ai/image/b0456826ee89ea952d83ed53f01c22ea/ee69f24dc56da81b169cf1087c33216f.png" 
                  alt="Estudiante estresado" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            
            <h2 className="text-3xl font-semibold text-gray-800 text-center mb-8">
              ¿Reconoces este escenario en casa?
            </h2>
            
            <div className="space-y-5 mb-8">
              <div className="flex items-start gap-4">
                <i className="ri-checkbox-circle-fill text-orange-600 text-2xl flex-shrink-0 mt-1"></i>
                <p className="text-lg text-gray-700">
                  Las tareas terminan en discusiones diarias que desgastan a toda la familia.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <i className="ri-checkbox-circle-fill text-orange-600 text-2xl flex-shrink-0 mt-1"></i>
                <p className="text-lg text-gray-700">
                  Estudiar la noche anterior a la prueba genera niveles altísimos de estrés y ansiedad.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <i className="ri-checkbox-circle-fill text-orange-600 text-2xl flex-shrink-0 mt-1"></i>
                <p className="text-lg text-gray-700">
                  Sabes que tu hij@ tiene potencial, pero tú ya no quieres (ni deberías) ser su 'jefe de estudios'.
                </p>
              </div>
            </div>
            
            <p className="text-xl text-orange-600 text-center italic font-medium">
              No tienen que hacerlo solos. Es hora de trazar una ruta clara.
            </p>
          </div>
        </div>
      </section>

      {/* Metodología Section */}
      <section id="metodologia" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              El Método de 'El Papá Profesor': Estructura, Foco y Resultados
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Un enfoque integral que deja atrás la improvisación para construir cimientos académicos sólidos durante todo el año.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Pilar 1 */}
            <div className="bg-white border border-gray-200 rounded-xl p-10 hover:shadow-xl transition-all duration-300">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-file-list-3-line text-4xl text-orange-600"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 text-center mb-4">
                Escuela Académica
              </h3>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Acompañamiento mes a mes. Seguimiento académico integral con máxima personalización. Grupos de máximo 3 estudiantes estrictamente del mismo curso, o planes individuales a tu medida.
              </p>
            </div>
            
            {/* Pilar 2 */}
            <div className="bg-white border border-gray-200 rounded-xl p-10 hover:shadow-xl transition-all duration-300">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-group-line text-4xl text-orange-600"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 text-center mb-4">
                Salvavidas: Prepara tu Prueba
              </h3>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Apoyo clave para evaluaciones. Sesiones intensivas 100% enfocadas en la materia de esa prueba importante, diseñadas para asegurar el resultado, despejar dudas y bajar la ansiedad del estudiante.
              </p>
            </div>
            
            {/* Pilar 3 */}
            <div className="bg-white border border-gray-200 rounded-xl p-10 hover:shadow-xl transition-all duration-300">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-24-hours-line text-4xl text-orange-600"></i>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 text-center mb-4">
                Material y Comunidad 24/7
              </h3>
              <p className="text-base text-gray-600 text-center leading-relaxed">
                Acceso ilimitado a material de estudio exclusivo post-clase, sesiones grabadas para repasar antes del gran día y comunicación directa y periódica contigo a través de nuestros canales oficiales.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonios Section */}
      <section id="testimonios" className="py-24 px-6 bg-amber-50/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Familias que Recuperaron la Tranquilidad
            </h2>
            <p className="text-lg text-gray-600">
              Testimonios reales de padres que lograron un año escolar exitoso
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Testimonio 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm relative">
              <div className="absolute top-6 left-6 text-6xl text-orange-200 opacity-50">
                <i className="ri-double-quotes-l"></i>
              </div>
              <p className="text-base text-gray-700 italic leading-relaxed mb-6 relative z-10" style={{ fontFamily: 'Georgia, serif' }}>
                Antes las tareas eran un martirio. Ahora mi hija llega motivada a sus clases y sus notas mejoraron increíblemente. Lo mejor es que volvió la paz a nuestra casa.
              </p>
              <div className="border-t-2 border-orange-300 w-10 my-5"></div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Friendly%20smiling%20Chilean%20mother%20portrait%20headshot%2C%20warm%20natural%20lighting%2C%20soft%20background%2C%20professional%20photo%2C%20approachable%20expression%2C%20casual%20elegant%20style%2C%20clean%20simple%20background&width=100&height=100&seq=avatar-maria-001&orientation=squarish" 
                    alt="María José Contreras" 
                    className="w-11 h-11 rounded-full object-cover object-top flex-shrink-0"
                  />
                  <div>
                    <p className="font-bold text-base text-gray-800">María José Contreras</p>
                    <p className="text-sm text-gray-600">Madre de Sofía, 7° Básico</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-green-100 px-3 py-1 rounded-full flex-shrink-0 ml-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-sm"></i>
                  <span className="text-xs font-semibold text-green-700">Verificado</span>
                </div>
              </div>
            </div>
            
            {/* Testimonio 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm relative">
              <div className="absolute top-6 left-6 text-6xl text-orange-200 opacity-50">
                <i className="ri-double-quotes-l"></i>
              </div>
              <p className="text-base text-gray-700 italic leading-relaxed mb-6 relative z-10" style={{ fontFamily: 'Georgia, serif' }}>
                Mi hijo estaba desmotivado y con notas bajas en matemáticas. Con El Papá Profesor no solo mejoró sus notas, sino que recuperó la confianza en sí mismo. Estoy muy agradecido.
              </p>
              <div className="border-t-2 border-orange-300 w-10 my-5"></div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Friendly%20smiling%20Chilean%20father%20portrait%20headshot%2C%20warm%20natural%20lighting%2C%20soft%20background%2C%20professional%20photo%2C%20approachable%20kind%20expression%2C%20casual%20style%2C%20clean%20simple%20background&width=100&height=100&seq=avatar-roberto-002&orientation=squarish" 
                    alt="Roberto Muñoz" 
                    className="w-11 h-11 rounded-full object-cover object-top flex-shrink-0"
                  />
                  <div>
                    <p className="font-bold text-base text-gray-800">Roberto Muñoz</p>
                    <p className="text-sm text-gray-600">Padre de Matías, 1° Medio</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-green-100 px-3 py-1 rounded-full flex-shrink-0 ml-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-sm"></i>
                  <span className="text-xs font-semibold text-green-700">Verificado</span>
                </div>
              </div>
            </div>
            
            {/* Testimonio 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-sm relative">
              <div className="absolute top-6 left-6 text-6xl text-orange-200 opacity-50">
                <i className="ri-double-quotes-l"></i>
              </div>
              <p className="text-base text-gray-700 italic leading-relaxed mb-6 relative z-10" style={{ fontFamily: 'Georgia, serif' }}>
                La metodología es excelente. Los profesores son pacientes y saben cómo explicar. Mi hija terminó el año con promedio 6.2 cuando antes tenía 4.8. ¡Totalmente recomendado!
              </p>
              <div className="border-t-2 border-orange-300 w-10 my-5"></div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img 
                    src="https://readdy.ai/api/search-image?query=Friendly%20smiling%20Chilean%20mother%20portrait%20headshot%2C%20warm%20soft%20lighting%2C%20gentle%20expression%2C%20professional%20photo%2C%20casual%20elegant%20look%2C%20clean%20simple%20light%20background%2C%20approachable%20warm%20face&width=100&height=100&seq=avatar-carolina-003&orientation=squarish" 
                    alt="Carolina Pérez" 
                    className="w-11 h-11 rounded-full object-cover object-top flex-shrink-0"
                  />
                  <div>
                    <p className="font-bold text-base text-gray-800">Carolina Pérez</p>
                    <p className="text-sm text-gray-600">Madre de Valentina, 5° Básico</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-green-100 px-3 py-1 rounded-full flex-shrink-0 ml-3">
                  <i className="ri-checkbox-circle-fill text-green-600 text-sm"></i>
                  <span className="text-xs font-semibold text-green-700">Verificado</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planes Section */}
      <section id="planes" className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
              Nuestros Programas de Apoyo Escolar
            </h2>
            <p className="text-lg text-gray-600">
              Elige el programa que mejor se adapte a las necesidades de tu hijo/a
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 items-start">
            {/* Tarjeta 1 - Escuela Tradicional (DESTACADA - PRIMERA POSICIÓN) */}
            <div className="bg-white border-2 border-orange-600 rounded-2xl p-10 shadow-xl relative">
              <div className="absolute -top-4 right-6 bg-orange-600 text-white px-4 py-1.5 rounded-full text-sm font-bold">
                Más Popular
              </div>
              
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-file-list-3-line text-4xl text-orange-600"></i>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800 text-center mb-4">
                Planes de acompañamiento
              </h3>
              
              <p className="text-base text-gray-600 text-center leading-relaxed mb-6">
                Tu tranquilidad mes a mes. Un proceso guiado que recupera la confianza del estudiante y devuelve la paz a la casa, sin compromisos anuales forzados.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Seguimiento académico integral del estudiante.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Grupos de máximo 3 alumnos (mismo nivel) o planes individuales.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Comunicación periódica y directa contigo.
                  </p>
                </div>
              </div>
              
              <a
                href="https://wa.me/56912345678?text=Hola%2C%20quiero%20solicitar%20un%20diagn%C3%B3stico%20para%20la%20Escuela%20Tradicional"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 bg-orange-600 text-white py-4 rounded-lg hover:bg-orange-700 transition-all font-semibold text-base shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                👉 Solicitar Diagnóstico por WhatsApp
              </a>
            </div>
            
            {/* Tarjeta 2 - Prepara tu Prueba (SEGUNDA POSICIÓN) */}
            <div className="bg-white border-2 border-gray-200 rounded-2xl p-10 hover:shadow-lg transition-all">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center">
                  <i className="ri-lifebuoy-line text-4xl text-orange-600"></i>
                </div>
              </div>
              
              <h3 className="text-2xl font-bold text-gray-800 text-center mb-4">
                Prepara tu Prueba
              </h3>
              
              <p className="text-base text-gray-600 text-center leading-relaxed mb-6">
                El salvavidas estratégico. Dirigido a estudiantes que necesitan un repaso intensivo antes de una evaluación clave para bajar la ansiedad y asegurar el resultado.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    <strong>Desde $6.000 (precios preferenciales si estás con un plan de acompañamiento activo).</strong>
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Sesiones 100% enfocadas en la materia de la evaluación.
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-orange-600 text-xl flex-shrink-0 mt-0.5"></i>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    Incluye material de estudio exclusivo post-clase y sesión grabada.
                  </p>
                </div>
              </div>
              
              <a
                href="https://wa.me/56912345678?text=Hola%2C%20quiero%20solicitar%20un%20diagn%C3%B3stico%20para%20Prepara%20tu%20Prueba"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 border-2 border-orange-600 text-orange-600 py-4 rounded-lg hover:bg-orange-50 transition-all font-semibold text-base whitespace-nowrap cursor-pointer"
              >
                👉 Solicitar Sesión por WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Comunidad Section */}
      <section id="comunidad" className="py-20 px-6 bg-gradient-to-br from-orange-500 to-orange-700">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 flex items-center justify-center">
              <i className="ri-whatsapp-line text-8xl text-white"></i>
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Únete a Nuestra Comunidad Gratuita
          </h2>
          
          <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-12 max-w-2xl mx-auto">
            Ingresa directo a nuestro canal de avisos oficiales. Una comunidad libre de spam y sin interrupciones para recibir tips de neurociencia, material de apoyo y acceder a <strong>PRECIOS PREFERENCIALES</strong> exclusivos en tus planes de acompañamiento y preparaciones de prueba del año.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-5 justify-center max-w-xl mx-auto mb-8">
            <a
              href="https://bit.ly/3M9EeuH"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-gray-800 px-8 py-5 rounded-xl hover:bg-gray-50 transition-all font-bold text-lg shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              🧸 Comunidad Básica (1° a 6°)
            </a>
            <a
              href="https://bit.ly/4kaK37Q"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-white text-gray-800 px-8 py-5 rounded-xl hover:bg-gray-50 transition-all font-bold text-lg shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              🚀 Comunidad Media (7° a 4° Medio)
            </a>
          </div>
          
          <p className="text-sm text-white/70">
            Cero interrupciones · Solo tips de valor y descuentos exclusivos
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 text-center mb-16">
            Preguntas Frecuentes
          </h2>
          
          <div className="space-y-3">
            {[
              {
                question: '¿Cómo funcionan los planes de acompañamiento individual?',
                answer: 'Los planes de acompañamiento individual constan de sesiones personalizadas de 60 minutos donde el profesor se enfoca 100% en tu hijo/a. Trabajamos los contenidos que necesita reforzar, preparamos pruebas específicas y adaptamos el ritmo a sus necesidades. Las clases pueden ser presenciales u online según tu preferencia.'
              },
              {
                question: '¿Qué incluye el material 24/7?',
                answer: 'Tendrás acceso a nuestra plataforma con clases grabadas, guías de estudio descargables, ejercicios resueltos paso a paso y recursos complementarios organizados por asignatura y nivel. Tu hijo/a puede estudiar cuando quiera, las veces que necesite.'
              },
              {
                question: '¿Cuántos estudiantes hay en las clases grupales?',
                answer: 'Nuestras clases grupales tienen un máximo de 3 estudiantes del mismo nivel y colegio. Esto nos permite mantener atención personalizada mientras los alumnos se benefician del aprendizaje colaborativo y la interacción con sus pares, a su vez que aseguramos que todos en el grupo están viendo lo mismo.'
              },
              {
                question: '¿Ofrecen evaluación diagnóstica gratuita?',
                answer: 'Sí. Para entrar a la familia de El Papá Profesor, necesitamos conocernos. Queremos evaluar personalmente qué necesita tu hij@ y diseñar su plan de trabajo a la medida. Es 100% gratuito, sin compromiso y sin letras chicas.'
              },
              {
                question: '¿Puedo cambiar de plan durante el año?',
                answer: 'Por supuesto. Entendemos que las necesidades pueden cambiar. Puedes actualizar o modificar tu plan en cualquier momento. Nuestro equipo te ayudará a encontrar la mejor opción según la evolución de tu hijo/a.'
              },
              {
                question: '¿Qué pasa si mi hijo/a no puede asistir a una clase?',
                answer: 'Si nos avisas con 24 horas de anticipación, reprogramamos la clase sin problema. Además, con el material 24/7, tu hijo/a puede seguir estudiando y no perder el ritmo aunque falte a una sesión.'
              },
              {
                question: '¿Cuáles son los medios de pago y cómo funciona el cobro?',
                answer: 'Para tu comodidad, aceptamos transferencias bancarias. Nos encontramos en proceso de incorporar otros medios de pago.\n\nEn el caso del Acompañamiento Continuo, el pago se realiza de forma mensual anticipada en los primeros 5 días del mes.\n\nPara el programa Prepara tu Prueba, el valor se abona al momento de reservar y confirmar el cupo de tu hijo/a.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <h4 className="text-lg font-semibold text-gray-800 pr-4">{faq.question}</h4>
                  <i className={`ri-${openFaq === index ? 'subtract' : 'add'}-line text-2xl text-orange-600 flex-shrink-0`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-base text-gray-600 leading-relaxed whitespace-pre-line">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12 mb-8">
            {/* Columna 1 */}
            <div>
              <img 
                src="https://static.readdy.ai/image/b0456826ee89ea952d83ed53f01c22ea/e364b9ef8d395931045c47454dbe130a.png" 
                alt="El Papá Profesor" 
                className="h-12 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-sm text-gray-400 leading-relaxed">
                Apoyo educativo personalizado para estudiantes de Básica y Media en Chile. Devolvemos la tranquilidad a tu hogar.
              </p>
            </div>
            
            {/* Columna 2 */}
            <div>
              <h4 className="text-base font-bold mb-4">Navegación</h4>
              <ul className="space-y-2">
                <li><a href="#metodologia" className="text-sm text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Metodología</a></li>
                <li><a href="#testimonios" className="text-sm text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Testimonios</a></li>
                <li><a href="#planes" className="text-sm text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Planes</a></li>
                <li><a href="#comunidad" className="text-sm text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">Comunidad</a></li>
                <li><a href="#faq" className="text-sm text-gray-400 hover:text-orange-500 transition-colors cursor-pointer">FAQ</a></li>
              </ul>
            </div>
            
            {/* Columna 3 */}
            <div>
              <h4 className="text-base font-bold mb-4">Síguenos</h4>
              <div className="flex gap-4">
                <a 
                  href="https://www.facebook.com/ElPapaProfesor" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-orange-600 hover:border-orange-600 transition-all cursor-pointer"
                >
                  <i className="ri-facebook-line text-xl"></i>
                </a>
                <a 
                  href="https://www.instagram.com/el_papa_profesor/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-orange-600 hover:border-orange-600 transition-all cursor-pointer"
                >
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a 
                  href="http://wa.me/56981357746" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-12 h-12 border-2 border-white rounded-full flex items-center justify-center hover:bg-orange-600 hover:border-orange-600 transition-all cursor-pointer"
                >
                  <i className="ri-whatsapp-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-gray-400 text-center md:text-left">
              © 2026 El Papá Profesor. Todos los derechos reservados.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-gray-400 hover:text-orange-500 transition-colors cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
